import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { TFunction } from 'i18next';
import { MediaNotFound } from '@/components/Message/MediaNotFound';
import { MediaHeading } from './MediaHeading';
import { Spinner } from '@/components/Loading/Spinner';

interface CheckPointPhotoProps {
  t?: TFunction<undefined>;
  title?: string;
  imageUrl: string;
  onClick?: () => void;
}

export const CheckPointPhoto = ({
  t,
  title,
  imageUrl,
  onClick,
}: CheckPointPhotoProps) => {
  const [imageIsLoading, setImageIsLoading] = useState(true);
  const [imageExists, setImageExists] = useState(true);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Reset states when imageUrl changes
  useEffect(() => {
    setImageIsLoading(true);
    setImageExists(true);
  }, [imageUrl]);

  // Check if image is already cached immediately after render
  useLayoutEffect(() => {
    if (imgRef.current) {
      const img = imgRef.current;
      // For cached images, complete is true and naturalWidth > 0
      if (img.complete && img.naturalWidth > 0) {
        setImageIsLoading(false);
        setImageExists(true);
      }
    }
  }, [imageUrl]);

  return (
    <div className="max-sm:w-full">
      <MediaHeading
        containerClassName="mb-1.5"
        defaultIconClassName="ki-outline ki-graph-3 text-primary font-bold text-lg shadow-sm"
        title={title}
      />
      <div
        className={twMerge(
          'relative w-[560px] h-[299px] flex justify-center items-center bg-black shadow-md hover:cursor-pointer max-sm:w-auto',
          imageIsLoading && 'hover:cursor-not-allowed',
          !imageIsLoading && !imageExists && 'hover:cursor-default',
        )}
        onClick={!imageIsLoading && imageExists ? onClick : undefined}
      >
        <img
          ref={imgRef}
          src={imageUrl}
          className="w-auto h-full object-contain object-center"
          alt="checkpoint"
          onLoad={() => {
            setImageIsLoading(false);
            setImageExists(true);
          }}
          onError={() => {
            setImageIsLoading(false);
            setImageExists(false);
          }}
        />
        {imageIsLoading ? (
          <div className="absolute w-full h-full bg-blue-131025 flex justify-center items-center">
            <Spinner />
          </div>
        ) : (
          !imageExists && (
            <MediaNotFound
              defaultIconClassName="-mt-2 text-[65px]"
              notFoundText={!imageExists ? t('common:noImageFound') : ''}
            />
          )
        )}
      </div>
    </div>
  );
};
